import { modsFrom } from '@gonzovnc/protocol';
import type { Session } from '../core/session.js';
import type { PointerButton, PointerIntent } from '../core/types.js';
import { containRect, normalizeToContent, type Rect } from '../core/geometry.js';

/**
 * Browser input capture (§6.9). Translates DOM pointer/keyboard/wheel events into
 * normalized intents fed to the DOM-free Session (encoder + coalescer). All DOM lives here.
 *
 * Desktop model = **Pointer Lock**: the controller clicks the stage to *capture* the mouse
 * into the page; the OS cursor is then hidden and confined, and mouse *movement deltas* drive
 * a virtual remote cursor (relative control) — so you never have to travel to the physical
 * screen edge to reach the remote screen edge, and there is a clear capture/release. **Esc
 * releases** the capture (browser-guaranteed). A local predicted cursor is drawn so movement
 * feels immediate despite network latency. This replaces the old absolute-position mapping.
 *
 * Touch model (§6.11) is unchanged: a fixed center crosshair with drag-pan / pinch-zoom.
 */
export class InputCapture {
  private surface: HTMLElement | null = null;
  private video: HTMLVideoElement | null = null;
  private keyboardSink: HTMLElement | null = null;
  private cursorEl: HTMLElement | null = null;
  private disp = '';
  private raf = 0;
  private active = false;
  private bound: Array<[EventTarget, string, EventListener]> = [];

  // Pointer-lock (desktop) state.
  private locked = false;
  private vx = 0.5; // virtual remote cursor position, normalized [0,1]
  private vy = 0.5;
  private lockCb: ((locked: boolean) => void) | null = null;

  // Touch mode (§6.11): a fixed on-screen crosshair at the stage center; the user pans/zooms
  // the shared screen UNDER it (1-finger drag pans, pinch zooms). A single tap is a left click,
  // a two-finger tap is a right click — both at the crosshair's current target. `tScale`/`tx`/`ty`
  // are the video CSS transform (about the stage center == crosshair).
  private touch = false;
  private tScale = 1;
  private tx = 0;
  private ty = 0;

  constructor(private session: Session) {}

  setDisplay(disp: string): void { this.disp = disp; }

  /** Element used to draw the local (predicted) cursor while pointer-locked. */
  setSyntheticCursor(el: HTMLElement | null): void { this.cursorEl = el; }

  /** Notified whenever pointer-lock engages/releases so the UI can toggle its prompt. */
  onLock(cb: (locked: boolean) => void): void { this.lockCb = cb; }

  get isLocked(): boolean { return this.locked; }

  /** Capture the mouse into the page (MUST be called from a user-gesture handler). */
  requestLock(): void {
    const el = this.surface as (HTMLElement & { requestPointerLock?: (o?: { unadjustedMovement?: boolean }) => Promise<void> | void }) | null;
    if (!el?.requestPointerLock) return;
    try {
      const p = el.requestPointerLock({ unadjustedMovement: true });
      if (p && typeof (p as Promise<void>).catch === 'function') {
        (p as Promise<void>).catch(() => { try { el.requestPointerLock!(); } catch { /* ignore */ } });
      }
    } catch { try { el.requestPointerLock(); } catch { /* ignore */ } }
  }

  /** Release the mouse but stay the controller (same effect as the user pressing Esc). */
  exitLock(): void { try { document.exitPointerLock?.(); } catch { /* ignore */ } }

  /**
   * Send a discrete key chord (down+up) with a modifier bitmask — used by the on-screen
   * special-keys bar for keys that mobile soft keyboards lack (Esc, Tab, ⌘/⌃/⌥ chords,
   * arrows, F-keys, …). The Host applies `mods` as CGEvent flags, so ⌘C == KeyC + mods:cmd.
   */
  tapKey(code: string, key: string, mods = 0): void {
    this.session.send(this.session.encoder.keyDown({ code, key, mods, repeat: false }));
    this.session.send(this.session.encoder.keyUp(code, mods));
  }

  /** Send typed text (from the hidden input that summons the mobile OS keyboard). */
  sendText(str: string): void {
    if (str) this.session.send(this.session.encoder.text(str));
  }

  /**
   * Register the hidden soft-keyboard sink. While it holds focus, the global keydown/keyup
   * capture yields to it, so mobile OS keyboards route through the sink's `beforeinput`
   * (reliable) instead of the physical-key path (soft keyboards emit `Unidentified` codes).
   */
  setKeyboardSink(el: HTMLElement | null): void { this.keyboardSink = el; }

  private sinkFocused(): boolean {
    return !!this.keyboardSink && document.activeElement === this.keyboardSink;
  }

  private mods(e: KeyboardEvent | MouseEvent | WheelEvent): number {
    return modsFrom({ shift: e.shiftKey, ctrl: e.ctrlKey, option: e.altKey, cmd: e.metaKey, capsLock: 'getModifierState' in e && (e as KeyboardEvent).getModifierState?.('CapsLock') });
  }

  /**
   * True when a pointer/touch event lands on overlay UI (the on-screen keys bar or keyboard
   * sink) rather than the shared screen. The capture listeners live on the stage, and native
   * events bubble up from these children, so without this guard tapping a special key would
   * ALSO inject a mouse click on the remote screen.
   */
  private onOverlay(e: Event): boolean {
    const t = e.target as Element | null;
    return !!t && t !== this.surface && t !== this.video && !!t.closest?.('.keysbar, .kbd-sink, .stage-overlay, .audio-unlock, .banner, .statsbox');
  }

  attach(surface: HTMLElement, opts: { touch?: boolean } = {}): void {
    this.detach();
    this.surface = surface;
    this.video = surface.querySelector('video');
    this.touch = !!opts.touch;
    this.active = true;

    const on = (target: EventTarget, type: string, fn: (e: any) => void) => {
      target.addEventListener(type, fn as EventListener, { passive: false });
      this.bound.push([target, type, fn as EventListener]);
    };

    if (this.touch) {
      this.attachTouch(surface, on);
    } else {
      this.attachPointerLock(surface, on);
    }

    // Keyboard: desktop forwards only while pointer-locked (so keys don't leak to the remote
    // before you've captured); touch routes typing through the sink. Esc is reserved for
    // "release capture" and is never forwarded (the browser exits pointer lock on Esc anyway).
    const keydown = (e: KeyboardEvent) => {
      if (e.isComposing || this.sinkFocused() || !this.locked || e.code === 'Escape') return;
      e.preventDefault();
      this.session.send(this.session.encoder.keyDown({ code: e.code, key: e.key, mods: this.mods(e), repeat: e.repeat }));
    };
    const keyup = (e: KeyboardEvent) => {
      if (e.isComposing || this.sinkFocused() || !this.locked || e.code === 'Escape') return;
      e.preventDefault();
      this.session.send(this.session.encoder.keyUp(e.code, this.mods(e)));
    };
    on(window, 'keydown', keydown);
    on(window, 'keyup', keyup);
    on(surface, 'compositionend', (e: CompositionEvent) => {
      if (e.data) this.session.send(this.session.encoder.text(e.data));
    });

    const reset = () => { this.session.coalescer.clear(); this.session.send(this.session.encoder.reset()); };
    on(window, 'blur', reset);
    on(document, 'visibilitychange', () => { if (document.visibilityState === 'hidden') reset(); });

    const tick = () => {
      this.session.coalescer.tick(performance.now());
      if (this.active) this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  // ---- Desktop: Pointer Lock (relative movement + predicted cursor) --------------------

  private attachPointerLock(surface: HTMLElement, on: (t: EventTarget, type: string, fn: (e: any) => void) => void): void {
    // The displayed (letterbox-aware) video rect, in viewport px.
    const contentPx = (): Rect => {
      const r = surface.getBoundingClientRect();
      const rect: Rect = { left: r.left, top: r.top, width: r.width, height: r.height };
      const v = this.video;
      return v && v.videoWidth > 0 && v.videoHeight > 0 ? containRect(rect, v.videoWidth, v.videoHeight) : rect;
    };
    const vIntent = (): PointerIntent => ({ disp: this.disp, x: Number(this.vx.toFixed(5)), y: Number(this.vy.toFixed(5)) });
    const placeCursor = () => {
      if (!this.cursorEl) return;
      const sr = surface.getBoundingClientRect();
      const c = contentPx();
      const px = (c.left - sr.left) + this.vx * c.width;
      const py = (c.top - sr.top) + this.vy * c.height;
      this.cursorEl.style.transform = `translate(${px}px, ${py}px)`;
    };

    on(surface, 'mousemove', (e: MouseEvent) => {
      if (!this.locked) return; // relative movement only once captured
      const c = contentPx();
      this.vx = Math.min(1, Math.max(0, this.vx + (e.movementX || 0) / (c.width || 1)));
      this.vy = Math.min(1, Math.max(0, this.vy + (e.movementY || 0) / (c.height || 1)));
      placeCursor();
      this.session.coalescer.sampleMove(vIntent());
    });
    on(surface, 'mousedown', (e: MouseEvent) => {
      if (this.onOverlay(e)) return;
      e.preventDefault();
      if (!this.locked) { this.requestLock(); return; } // first click captures; it does not inject
      this.session.send(this.session.encoder.pointerDown(vIntent(), (e.button as PointerButton) ?? 0, e.detail || 1));
    });
    on(surface, 'mouseup', (e: MouseEvent) => {
      if (!this.locked || this.onOverlay(e)) return;
      e.preventDefault();
      this.session.send(this.session.encoder.pointerUp(vIntent(), (e.button as PointerButton) ?? 0));
    });
    on(surface, 'wheel', (e: WheelEvent) => {
      if (!this.locked) return;
      e.preventDefault();
      const factor = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 800 : 1;
      this.session.coalescer.sampleWheel(vIntent(), e.deltaX * factor, e.deltaY * factor, this.mods(e));
    });
    on(surface, 'contextmenu', (e: Event) => e.preventDefault());

    on(document, 'pointerlockchange', () => {
      this.locked = document.pointerLockElement === surface;
      if (this.locked) { placeCursor(); }
      else { this.session.coalescer.clear(); this.session.send(this.session.encoder.reset()); }
      this.lockCb?.(this.locked);
    });
    on(document, 'pointerlockerror', () => { this.locked = false; this.lockCb?.(false); });
  }

  /** Enter fullscreen + Keyboard Lock (Chromium only; §6.12). Used by touch mode. Best-effort.
   *  `target` lets the caller fullscreen a larger container (e.g. the whole room, so side
   *  panels stay reachable); defaults to the capture surface. */
  async enterImmersive(target?: HTMLElement): Promise<void> {
    try { await (target ?? this.surface)?.requestFullscreen?.(); } catch { /* ignore */ }
    const kb = (navigator as unknown as { keyboard?: { lock?: (keys?: string[]) => Promise<void> } }).keyboard;
    try { await kb?.lock?.(); } catch { /* Safari/Firefox: unsupported, documented */ }
  }

  // ---- Touch mode: crosshair + pan/zoom + tap gestures ---------------------------------

  private stageMetrics(): { O: { x: number; y: number }; base: Rect } {
    const r = this.surface!.getBoundingClientRect();
    const rect: Rect = { left: r.left, top: r.top, width: r.width, height: r.height };
    const v = this.video;
    const base = v && v.videoWidth > 0 && v.videoHeight > 0 ? containRect(rect, v.videoWidth, v.videoHeight) : rect;
    return { O: { x: r.left + r.width / 2, y: r.top + r.height / 2 }, base };
  }

  /** The remote point currently under the fixed center crosshair, given the pan/zoom transform. */
  private crosshairIntent(): PointerIntent {
    const { O, base } = this.stageMetrics();
    const px = O.x - this.tx / this.tScale;
    const py = O.y - this.ty / this.tScale;
    const { x, y } = normalizeToContent(px, py, base);
    return { disp: this.disp, x, y };
  }

  private applyTransform(): void {
    if (this.video) this.video.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.tScale})`;
  }

  private resetTransform(): void {
    this.tScale = 1; this.tx = 0; this.ty = 0;
    if (this.video) this.video.style.transform = '';
  }

  /** Keep the crosshair over the video (don't let the user pan the screen entirely away). */
  private clampPan(): void {
    const { base } = this.stageMetrics();
    const maxX = (base.width / 2) * this.tScale;
    const maxY = (base.height / 2) * this.tScale;
    this.tx = Math.min(maxX, Math.max(-maxX, this.tx));
    this.ty = Math.min(maxY, Math.max(-maxY, this.ty));
  }

  private crosshairClick(btn: PointerButton): void {
    const p = this.crosshairIntent();
    this.session.send(this.session.encoder.pointerDown(p, btn, 1));
    this.session.send(this.session.encoder.pointerUp(p, btn));
  }

  private attachTouch(surface: HTMLElement, on: (el: EventTarget, type: string, fn: (e: any) => void) => void): void {
    let fingers = 0;
    let sx = 0, sy = 0, sTx = 0, sTy = 0, sDist = 0, sScale = 1;
    let moved = false, startT = 0;
    const now = () => performance.now();
    const dist = (t: TouchList) => Math.hypot(t[0]!.clientX - t[1]!.clientX, t[0]!.clientY - t[1]!.clientY);
    const mid = (t: TouchList) => ({ x: (t[0]!.clientX + t[1]!.clientX) / 2, y: (t[0]!.clientY + t[1]!.clientY) / 2 });

    on(surface, 'touchstart', (e: TouchEvent) => {
      if (this.onOverlay(e)) return;
      e.preventDefault();
      moved = false; startT = now(); fingers = e.touches.length;
      if (fingers === 1) { sx = e.touches[0]!.clientX; sy = e.touches[0]!.clientY; sTx = this.tx; sTy = this.ty; }
      else if (fingers >= 2) { sDist = dist(e.touches); sScale = this.tScale; const m = mid(e.touches); sx = m.x; sy = m.y; sTx = this.tx; sTy = this.ty; }
    });
    on(surface, 'touchmove', (e: TouchEvent) => {
      if (this.onOverlay(e)) return;
      e.preventDefault();
      if (e.touches.length === 1 && fingers === 1) {
        const dx = e.touches[0]!.clientX - sx, dy = e.touches[0]!.clientY - sy;
        if (Math.hypot(dx, dy) > 8) moved = true;
        this.tx = sTx + dx; this.ty = sTy + dy; this.clampPan(); this.applyTransform();
        this.session.coalescer.sampleMove(this.crosshairIntent()); // remote cursor tracks the crosshair
      } else if (e.touches.length >= 2) {
        const d = dist(e.touches);
        if (Math.abs(d - sDist) > 8) moved = true;
        this.tScale = Math.min(4, Math.max(1, sScale * (d / (sDist || 1))));
        const m = mid(e.touches);
        this.tx = sTx + (m.x - sx); this.ty = sTy + (m.y - sy); this.clampPan(); this.applyTransform();
      }
    });
    const end = (e: TouchEvent) => {
      if (this.onOverlay(e)) return;
      e.preventDefault();
      const quick = now() - startT < 300;
      if (!moved && quick) {
        if (fingers === 1) this.crosshairClick(0);      // single tap -> left click
        else if (fingers >= 2) this.crosshairClick(2);  // two-finger tap -> right click
      }
      fingers = e.touches.length;
      if (fingers === 1) { sx = e.touches[0]!.clientX; sy = e.touches[0]!.clientY; sTx = this.tx; sTy = this.ty; moved = false; startT = now(); }
    };
    on(surface, 'touchend', end);
    on(surface, 'touchcancel', end);
  }

  detach(): void {
    this.active = false;
    this.resetTransform();
    if (this.locked) { try { document.exitPointerLock?.(); } catch { /* ignore */ } }
    this.locked = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    for (const [target, type, fn] of this.bound) target.removeEventListener(type, fn);
    this.bound = [];
    this.surface = null;
    this.video = null;
    this.cursorEl = null;
  }
}
